import { ApolloClient } from '@apollo/client';
import { useCallback } from 'react';
import type { MutationShareArgs } from 'sr-types/lib/note/v1/graphql';
import { Reference } from 'sr-types/lib/search/v1/graphql';
import type { UserProfile } from '../../auth/api';
import { FETCH_POLICY_CONFIG } from '../../constants/constants';
import { useMutationWithContext } from '../../hooks/useMutationWithContext';
import { SaveShareSettingsMutationError, SaveShareSettingsServerError } from '../errors';
import type { ShareSettingsViewModel } from '../types';
import { shareMutation } from './shareGraphqlApi';
import { shareSettingsToNotificationInput, shareSettingsToShareSettingsInput } from './utils';

export type UseSaveShareSettingsApiOptions = {
    client: ApolloClient<object>;
    enableNotification?: boolean;
};

export function useSaveShareSettingsApi(options: UseSaveShareSettingsApiOptions) {
    const { client, enableNotification = true } = options;
    // const deepLinkUIStatePath = useEntitySecurityPath();
    // const { getDeepLinkURL } = useDeepLinkURL();

    const [shareMutationFn, { loading: isSavingShareSettings }] = useMutationWithContext(shareMutation, client);

    const saveShareSettingsApi = useCallback(
        async (
            entityReference: Reference,
            shareSettings: ShareSettingsViewModel,
            userProfile: UserProfile,
            existingSharedWithAccountIds: string[] = [],
            deepLinkURL?: string
        ): Promise<void> => {
            const input = shareSettingsToShareSettingsInput(shareSettings, userProfile);
            const restricted = input.restricted ?? false;
            const sharedWithAccounts = restricted ? input.sharedWithAccounts : undefined;
            const sharedWithDepartments = restricted ? input.sharedWithDepartments : undefined;

            const variables: MutationShareArgs = {
                input: {
                    id: entityReference.id,
                    restricted,
                    sharedWithAccounts,
                    sharedWithDepartments
                }
            };

            if (enableNotification) {
                const existingAccountIds = restricted ? existingSharedWithAccountIds : [];
                const notificationInput = shareSettingsToNotificationInput(
                    shareSettings,
                    input.sharedWithAccounts ?? [],
                    existingAccountIds
                );

                if (deepLinkURL) {
                    notificationInput.deepLinkURL = deepLinkURL;
                }

                variables.notificationInput = notificationInput;
            }

            const mutationFetchResult = await shareMutationFn({
                variables,
                fetchPolicy: FETCH_POLICY_CONFIG.apolloFetchPolicy,
                errorPolicy: 'all'
            });

            if (mutationFetchResult.errors?.length > 0) {
                throw new SaveShareSettingsMutationError(mutationFetchResult.errors[0].message);
            }

            if (mutationFetchResult.data?.share?.errors?.length > 0) {
                const err = mutationFetchResult.data.share.errors[0];
                throw new SaveShareSettingsServerError(err.message ?? err.key);
            }
        },
        [enableNotification, shareMutationFn]
    );

    return {
        saveShareSettingsApi,
        isSavingShareSettings
    };
}
